import type { Theme } from '@mui/material/styles'

type Rgb = { r: number; g: number; b: number }

const LUMINANCE_THRESHOLD = 186
const important = (value: string) => `${value} !important`

function parseHexChannel(pair: string): number {
  return parseInt(pair.length === 1 ? pair + pair : pair, 16)
}

function parseColorToRgb(color: string): Rgb | null {
  const trimmed = color.trim()
  const hex6 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(trimmed)
  if (hex6) {
    return { r: parseHexChannel(hex6[1]), g: parseHexChannel(hex6[2]), b: parseHexChannel(hex6[3]) }
  }

  const hex3 = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(trimmed)
  if (hex3) {
    return { r: parseHexChannel(hex3[1]), g: parseHexChannel(hex3[2]), b: parseHexChannel(hex3[3]) }
  }

  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(trimmed)
  return rgb ? { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) } : null
}

export const parseHighlightColorToRgb = parseColorToRgb

function luminance({ r, g, b }: Rgb): number {
  return r * 0.299 + g * 0.587 + b * 0.114
}

/** Contrasting text on an RGB background (black or white). */
export function textColorOnRgb(r: number, g: number, b: number): string {
  return luminance({ r, g, b }) > LUMINANCE_THRESHOLD ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'
}

/** Contrasting label colour on org highlight / brand background (checkout CTAs, timeline icons). */
export function textColorOnHighlightBackground(highlightColor: string): string {
  const rgb = parseColorToRgb(highlightColor)
  return rgb ? textColorOnRgb(rgb.r, rgb.g, rgb.b) : 'rgb(255, 255, 255)'
}

/** Muted disabled label — readable on the washed-out disabled fill (30% brand + 70% neutral grey). */
export function disabledForegroundOnHighlight(_highlightColor: string, theme: Theme): string {
  // Disabled backgrounds are always lightened via color-mix with neutralGrey[200], so use dark muted text
  // regardless of the enabled-state contrast on the full-strength brand colour.
  return String(theme.palette.neutralGrey[1000])
}

export function getHighlightButtonStyles(theme: Theme, highlightColor: string) {
  const trimmed = highlightColor.trim()
  const fg = textColorOnHighlightBackground(trimmed)
  const enabled = { backgroundColor: important(trimmed), color: important(fg) }
  const disabled = {
    backgroundColor: important(`color-mix(in srgb, ${trimmed} 30%, ${theme.palette.neutralGrey[200]})`),
    color: important(disabledForegroundOnHighlight(trimmed, theme)),
    filter: 'none',
    cursor: 'not-allowed',
  }

  return {
    '&&': enabled,
    ...enabled,
    '&:hover': { ...enabled, filter: 'brightness(0.92)' },
    '&.Mui-disabled, &.Mui-disabled:hover, &:disabled, &:disabled:hover': disabled,
  }
}
